import type { Checker } from "./mapcheck";

// Moving vehicles (#37, skitching): a cart driving round a loop, over and
// over, on the park's flat floor. Where it is is a pure function of the
// sim's clock (`Skater.clock`), so a demo line or a test sees it in the same
// place every time and the renderer just asks. Sim-free and three.js-free.
//
// A loop is a stadium: two straights `len` long, `2r` apart, joined by
// half circles of radius `r`, centred on (x, z) and turned by `rot` (local
// u along the straights, w across, like a primitive's frame). The cart
// drives it one way round: +u along the w = +r straight, so every turn is
// a left turn (yaw goes up).

export type VehicleKind = "cart";

/** A kind's size: half-length, half-width, roof height (m). */
export interface VehicleSize {
  hl: number;
  hw: number;
  h: number;
}

export const VEHICLE_KINDS: Readonly<Record<VehicleKind, VehicleSize>> = {
  // A beach buggy / golf cart: small enough to share a park with skaters.
  cart: { hl: 1.3, hw: 0.8, h: 1.7 },
};

export interface Vehicle extends VehicleSize {
  kind: VehicleKind;
  /** The loop's centre and turn (radians). */
  x: number;
  z: number;
  rot: number;
  /** Each straight's length, and the turns' radius (m). */
  len: number;
  r: number;
  /** m/s along the loop. */
  speed: number;
  /** How far along the loop it is at clock 0 (m). */
  start: number;
}

/** A vehicle as a map file writes it: `rot` in degrees, `start` optional. */
export interface MapVehicle {
  kind: VehicleKind;
  x: number;
  z: number;
  rot?: number;
  len: number;
  r: number;
  speed: number;
  start?: number;
}

/** Where a vehicle is: its centre on the floor, heading (`yaw`, nose along (sin, cos)) and how fast its yaw turns per metre. */
export interface VehiclePose {
  x: number;
  z: number;
  yaw: number;
  /** 1/r on a turn (always left), 0 on a straight. */
  curve: number;
}

/** Bounds a map file's vehicles have to keep to. */
export const VEHICLE_LIMITS = { minR: 3, maxR: 30, maxLen: 80, minSpeed: 2, maxSpeed: 12, maxCount: 4 } as const;

/** The loop's length (m). */
export function loopLength(v: Pick<Vehicle, "len" | "r">): number {
  return 2 * v.len + 2 * Math.PI * v.r;
}

/** Where `v` is at `s` metres along its loop (wrapped). */
export function poseAlong(v: Pick<Vehicle, "x" | "z" | "rot" | "len" | "r">, s: number): VehiclePose {
  const { len, r } = v;
  const total = loopLength(v);
  s = ((s % total) + total) % total;
  let u: number;
  let w: number;
  let hu: number;
  let hw: number;
  let curve = 0;
  if (s < len) {
    u = -len / 2 + s;
    w = r;
    hu = 1;
    hw = 0;
  } else if (s < len + Math.PI * r) {
    const a = (s - len) / r;
    u = len / 2 + r * Math.sin(a);
    w = r * Math.cos(a);
    hu = Math.cos(a);
    hw = -Math.sin(a);
    curve = 1 / r;
  } else if (s < 2 * len + Math.PI * r) {
    u = len / 2 - (s - len - Math.PI * r);
    w = -r;
    hu = -1;
    hw = 0;
  } else {
    const a = (s - 2 * len - Math.PI * r) / r;
    u = -len / 2 - r * Math.sin(a);
    w = -r * Math.cos(a);
    hu = -Math.cos(a);
    hw = Math.sin(a);
    curve = 1 / r;
  }
  const c = Math.cos(v.rot);
  const sn = Math.sin(v.rot);
  const hx = hu * c - hw * sn;
  const hz = hu * sn + hw * c;
  return { x: v.x + u * c - w * sn, z: v.z + u * sn + w * c, yaw: Math.atan2(hx, hz), curve };
}

/** Where `v` is at sim time `t`. */
export function vehiclePose(v: Vehicle, t: number): VehiclePose {
  return poseAlong(v, v.start + v.speed * t);
}

/** (x, z) in a vehicle's frame at `pose`: `along` its nose, `side` to its right. */
export function vehicleLocal(pose: VehiclePose, x: number, z: number): { along: number; side: number } {
  const fx = Math.sin(pose.yaw);
  const fz = Math.cos(pose.yaw);
  const dx = x - pose.x;
  const dz = z - pose.z;
  return { along: dx * fx + dz * fz, side: -dx * fz + dz * fx };
}

/** Compiles a map file's vehicle. */
export function buildVehicle(m: MapVehicle): Vehicle {
  return {
    ...VEHICLE_KINDS[m.kind],
    kind: m.kind,
    x: m.x,
    z: m.z,
    rot: ((m.rot ?? 0) * Math.PI) / 180,
    len: m.len,
    r: m.r,
    speed: m.speed,
    start: m.start ?? 0,
  };
}

/** The map file's `vehicles` list (where the loop runs is `playcheck`'s job). */
export function checkVehicles(c: Checker, v: unknown, path: string, bounds: number): void {
  if (!Array.isArray(v) || v.length > VEHICLE_LIMITS.maxCount) {
    c.fail(path, `expected a list of at most ${VEHICLE_LIMITS.maxCount} vehicles`);
    return;
  }
  v.forEach((e, i) => {
    const at = `${path}[${i}]`;
    const o = c.obj(e, at);
    if (!o) return;
    c.only(o, ["kind", "x", "z", "rot", "len", "r", "speed", "start"], at);
    if (!Object.hasOwn(VEHICLE_KINDS, o.kind as string)) c.fail(`${at}.kind`, `expected one of ${Object.keys(VEHICLE_KINDS).join(", ")}`);
    const inside = { min: -bounds, max: bounds };
    c.num(o, "x", at, inside);
    c.num(o, "z", at, inside);
    c.num(o, "rot", at, { min: -360, max: 360, optional: true });
    c.num(o, "len", at, { min: 0, max: VEHICLE_LIMITS.maxLen });
    c.num(o, "r", at, { min: VEHICLE_LIMITS.minR, max: VEHICLE_LIMITS.maxR });
    c.num(o, "speed", at, { min: VEHICLE_LIMITS.minSpeed, max: VEHICLE_LIMITS.maxSpeed });
    c.num(o, "start", at, { min: 0, optional: true });
  });
}
